import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import DashboardFilters from "@/components/DashboardFilters";
import { useTheme } from "@/contexts/ThemeContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  FolderOpen, Users, IndianRupee, RefreshCw, Search,
  ArrowUpDown, ChevronDown, ChevronUp, ChevronRight, ChevronLeft,
  BarChart3, Trophy, FileText, Pencil, Save, X, Calendar, Clock,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPie, Pie, Cell,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ["#6366f1","#06b6d4","#10b981","#f59e0b","#ef4444","#ec4899","#8b5cf6","#14b8a6","#f97316","#84cc16"];

const chartGrid = (dark) => dark ? "#1e2231" : "#f1f5f9";
const chartAxis = (dark) => dark ? "#64748b" : "#94a3b8";
const chartTooltip = (dark) => ({
  borderRadius: "12px",
  border: dark ? "1px solid #1e2231" : "1px solid #e2e8f0",
  fontSize: 13,
  backgroundColor: dark ? "#12151e" : "#fff",
  color: dark ? "#e8ecf1" : "#0f172a",
});

const fmt = (v) => {
  if (v >= 100000) return `\u20B9${(v / 100000).toFixed(2)}L`;
  if (v >= 1000) return `\u20B9${(v / 1000).toFixed(1)}K`;
  return `\u20B9${Math.round(v).toLocaleString("en-IN")}`;
};
const fmtFull = (v) => `\u20B9${Math.round(v).toLocaleString("en-IN")}`;

const fmtDate = (d) => {
  if (!d) return "-";
  try {
    const str = String(d).trim();
    const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
    
    // Handle "dd/mm/yyyy" or "dd/mm/yyyy, HH:MM:SS" format
    if (str.includes("/")) {
      const datePart = str.split(",")[0].trim(); // Remove time if present
      const parts = datePart.split("/");
      if (parts.length >= 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // 0-indexed
        const year = parseInt(parts[2], 10);
        if (!isNaN(day) && !isNaN(month) && !isNaN(year) && month >= 0 && month < 12) {
          return `${day} ${months[month]} ${year}`;
        }
      }
    }
    
    // Handle "2026-05-03 05:30:00" ISO format by replacing space with T
    const isoStr = str.replace(" ", "T");
    const dt = new Date(isoStr);
    if (isNaN(dt.getTime())) return str;
    return `${dt.getDate()} ${months[dt.getMonth()]} ${dt.getFullYear()}`;
  } catch { return String(d); }
};

// Extract base project name (strip trailing " S\d+")
const extractBase = (title) => {
  if (!title) return "Unknown";
  const m = title.match(/^(.+?)\s+S(\d+)\s*$/);
  return m ? m[1] : title;
};

const ProjectDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [resourceMappings, setResourceMappings] = useState([]);
  const [tableSearch, setTableSearch] = useState("");
  const [sortField, setSortField] = useState("total_amount");
  const [sortDir, setSortDir] = useState("desc");

  // Navigation state: null → project list, {project} → project detail, {project, resource} → resource tasks
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState("all");
  const [selectedResource, setSelectedResource] = useState(null);

  // Task edit
  const [editingTask, setEditingTask] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [filtered, setFiltered] = useState(null);
  const [filterParams, setFilterParams] = useState({});
  const { dark } = useTheme();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tasksRes, mappingsRes] = await Promise.all([
        axios.get(`${API}/tasks?limit=10000`),
        axios.get(`${API}/resource-mappings`).catch(() => ({ data: { mappings: [] } })),
      ]);
      setTasks(tasksRes.data.tasks || []);
      setResourceMappings(mappingsRes.data?.mappings || []);
    } catch (error) {
      toast.error("Failed to load project data");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFiltered = useCallback(async (params) => {
    try {
      const qp = new URLSearchParams(params);
      const res = await axios.get(`${API}/dashboard/filtered?${qp}`);
      setFiltered(res.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchFiltered(filterParams); }, [filterParams, fetchFiltered]);

  const handleFiltersChange = useCallback((params) => { setFilterParams(params); }, []);

  // Override chart/KPIs with filtered data
  const fTopProjects = useMemo(() => {
    if (filtered?.has_filters && filtered.top_projects?.length > 0) {
      return filtered.top_projects.map(p => ({
        name: p.project.length > 22 ? p.project.slice(0, 20) + "..." : p.project,
        fullName: p.project,
        amount: p.amount,
      }));
    }
    return null;
  }, [filtered]);

  const nameMap = useMemo(() => {
    const m = {};
    resourceMappings.forEach(r => { if (r.email && r.display_name) m[r.email] = r.display_name; });
    return m;
  }, [resourceMappings]);

  const displayName = useCallback((email) => nameMap[email] || email?.replace(/@graphikpro\.com$/, "") || "Unknown", [nameMap]);
  const completedTasks = useMemo(() => tasks.filter(t => t.task_status === "Completed"), [tasks]);

  /* ---- Group by Base Project Name ---- */
  const projects = useMemo(() => {
    const map = {};
    completedTasks.forEach(t => {
      const base = extractBase(t.main_title);
      const season = t.season_number ? String(t.season_number) : "Unknown";
      if (!map[base]) map[base] = { title: base, total_amount: 0, task_count: 0, total_duration: 0, resources: {}, seasons: {}, task_types: {} };
      const amt = t.amount || 0;
      const dur = t.duration_words || 0;
      map[base].total_amount += amt;
      map[base].task_count += 1;
      map[base].total_duration += dur;

      // Resource tracking
      const r = t.resource || "Unknown";
      if (!map[base].resources[r]) map[base].resources[r] = { email: r, amount: 0, tasks: 0, duration: 0 };
      map[base].resources[r].amount += amt;
      map[base].resources[r].tasks += 1;
      map[base].resources[r].duration += dur;

      // Season tracking
      if (!map[base].seasons[season]) map[base].seasons[season] = { season, amount: 0, tasks: 0, duration: 0, resources: {} };
      map[base].seasons[season].amount += amt;
      map[base].seasons[season].tasks += 1;
      map[base].seasons[season].duration += dur;
      if (!map[base].seasons[season].resources[r]) map[base].seasons[season].resources[r] = { email: r, amount: 0, tasks: 0, duration: 0 };
      map[base].seasons[season].resources[r].amount += amt;
      map[base].seasons[season].resources[r].tasks += 1;
      map[base].seasons[season].resources[r].duration += dur;

      // Task type
      const tt = t.task || "Other";
      if (!map[base].task_types[tt]) map[base].task_types[tt] = { name: tt, count: 0, amount: 0 };
      map[base].task_types[tt].count += 1;
      map[base].task_types[tt].amount += amt;
    });

    return Object.values(map).map(p => {
      const resourceList = Object.values(p.resources);
      const topEarner = resourceList.length > 0 ? resourceList.reduce((a, b) => a.amount > b.amount ? a : b) : null;
      const topContributor = resourceList.length > 0 ? resourceList.reduce((a, b) => a.tasks > b.tasks ? a : b) : null;
      const seasonList = Object.values(p.seasons).sort((a, b) => {
        const an = parseInt(a.season) || 0, bn = parseInt(b.season) || 0;
        return an - bn;
      });
      return {
        ...p,
        resource_count: resourceList.length,
        season_count: seasonList.length,
        top_earner: topEarner ? { name: displayName(topEarner.email), email: topEarner.email, amount: topEarner.amount } : null,
        top_contributor: topContributor ? { name: displayName(topContributor.email), email: topContributor.email, tasks: topContributor.tasks } : null,
        resourceList,
        seasonList,
        taskTypeList: Object.values(p.task_types).sort((a, b) => b.amount - a.amount),
      };
    });
  }, [completedTasks, displayName]);

  const totalProjects = projects.length;
  const totalExpense = useMemo(() => projects.reduce((s, p) => s + p.total_amount, 0), [projects]);
  const avgPerProject = totalProjects > 0 ? totalExpense / totalProjects : 0;
  const topProject = useMemo(() => projects.length > 0 ? [...projects].sort((a, b) => b.total_amount - a.total_amount)[0] : null, [projects]);

  const topProjectsChart = useMemo(() =>
    [...projects].sort((a, b) => b.total_amount - a.total_amount).slice(0, 10).map(p => ({
      name: p.title.length > 22 ? p.title.slice(0, 20) + "..." : p.title,
      fullName: p.title,
      amount: p.total_amount,
    })),
  [projects]);

  /* ---- Table sorting ---- */
  // Override project list with filtered API data when filters active
  const activeProjectList = useMemo(() => {
    if (filtered?.has_filters && filtered.all_projects?.length > 0) {
      return filtered.all_projects.map(p => ({
        title: p.project,
        total_amount: p.amount,
        task_count: p.count,
        total_duration: p.duration,
        resource_count: p.resources,
        season_count: 0,
        top_earner: null,
        top_contributor: null,
        resourceList: [],
        seasonList: [],
        taskTypeList: [],
      }));
    }
    return projects;
  }, [filtered, projects]);

  const filteredProjects = useMemo(() => {
    let data = [...activeProjectList];
    if (tableSearch) {
      const q = tableSearch.toLowerCase();
      data = data.filter(p => p.title.toLowerCase().includes(q));
    }
    data.sort((a, b) => sortDir === "desc" ? b[sortField] - a[sortField] : a[sortField] - b[sortField]);
    return data;
  }, [activeProjectList, tableSearch, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "desc" ? "asc" : "desc");
    else { setSortField(field); setSortDir("desc"); }
  };
  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 text-slate-400" />;
    return sortDir === "desc" ? <ChevronDown className="w-3 h-3 text-indigo-500" /> : <ChevronUp className="w-3 h-3 text-indigo-500" />;
  };

  /* ---- Project Detail Data ---- */
  const projectDetail = useMemo(() => {
    if (!selectedProject) return null;
    return projects.find(p => p.title === selectedProject) || null;
  }, [selectedProject, projects]);

  const detailResources = useMemo(() => {
    if (!projectDetail) return [];
    let resources;
    if (selectedSeason === "all") {
      resources = projectDetail.resourceList;
    } else {
      const seasonData = projectDetail.seasons[selectedSeason];
      resources = seasonData ? Object.values(seasonData.resources) : [];
    }
    return resources.map(r => ({ ...r, name: displayName(r.email) })).sort((a, b) => b.amount - a.amount);
  }, [projectDetail, selectedSeason, displayName]);

  const detailPieData = useMemo(() => {
    if (!projectDetail) return [];
    return projectDetail.taskTypeList.map((t, i) => ({ name: t.name, value: t.amount, color: COLORS[i % COLORS.length] }));
  }, [projectDetail]);

  const detailTotalAmount = useMemo(() => {
    if (selectedSeason === "all") return projectDetail?.total_amount || 0;
    return projectDetail?.seasons[selectedSeason]?.amount || 0;
  }, [projectDetail, selectedSeason]);

  /* ---- Resource Task List ---- */
  const resourceTasks = useMemo(() => {
    if (!selectedProject || !selectedResource) return [];
    return completedTasks.filter(t => {
      const base = extractBase(t.main_title);
      if (base !== selectedProject) return false;
      if (t.resource !== selectedResource) return false;
      if (selectedSeason !== "all" && String(t.season_number) !== selectedSeason) return false;
      return true;
    });
  }, [completedTasks, selectedProject, selectedResource, selectedSeason]);

  /* ---- Task Edit ---- */
  const openEdit = (task) => {
    setEditingTask(task);
    setEditData({
      duration_words: task.duration_words || "",
      rate: task.rate || "",
      rate_type: task.rate_type || "per_unit",
      episode_title: task.episode_title || "",
      episode_number: task.episode_number || "",
      season_number: task.season_number || "",
      task: task.task || "",
      language: task.language || "",
    });
  };

  const saveEdit = async () => {
    if (!editingTask) return;
    setSaving(true);
    try {
      const payload = {
        duration_words: editData.duration_words ? parseFloat(editData.duration_words) : null,
        rate: editData.rate ? parseFloat(editData.rate) : null,
        rate_type: editData.rate_type || "per_unit",
        episode_title: editData.episode_title || null,
        episode_number: editData.episode_number || null,
        season_number: editData.season_number || null,
        task: editData.task || null,
        language: editData.language || null,
      };
      await axios.put(`${API}/tasks/${editingTask.id}`, payload);
      toast.success("Task updated");
      setEditingTask(null);
      fetchData();
    } catch {
      toast.error("Failed to update task");
    } finally {
      setSaving(false);
    }
  };

  const editAmount = useMemo(() => {
    if (!editData.rate) return 0;
    const rate = parseFloat(editData.rate) || 0;
    if (editData.rate_type === "flat") return rate;
    const dur = parseFloat(editData.duration_words) || 0;
    return dur * rate;
  }, [editData]);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64" data-testid="project-dashboard-loading">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
          <p className="text-slate-500 text-sm">Loading project data...</p>
        </div>
      </div>
    );
  }

  /* =============================== */
  /* LEVEL 3: Resource Task List     */
  /* =============================== */
  if (selectedProject && selectedResource) {
    const resName = displayName(selectedResource);
    return (
      <div className="p-6" data-testid="resource-task-view">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="outline" size="sm" onClick={() => setSelectedResource(null)} data-testid="back-to-project-btn">
            <ChevronLeft className="w-4 h-4 mr-1" />Back to {selectedProject}
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900">{resName}</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Tasks on <span className="font-medium">{selectedProject}</span>
              {selectedSeason !== "all" && <span> / Season {selectedSeason}</span>}
              <span className="ml-2 text-slate-400">({resourceTasks.length} tasks)</span>
            </p>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-gradient-to-br from-indigo-500 to-violet-600 rounded-2xl p-4 text-white" data-testid="res-expense-card">
            <p className="text-xs text-white/70 mb-1">Total Expense</p>
            <p className="text-xl font-bold">{fmtFull(resourceTasks.reduce((s, t) => s + (t.amount || 0), 0))}</p>
          </div>
          <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-4 text-white" data-testid="res-tasks-card">
            <p className="text-xs text-white/70 mb-1">Tasks</p>
            <p className="text-xl font-bold">{resourceTasks.length}</p>
          </div>
          <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl p-4 text-white" data-testid="res-duration-card">
            <p className="text-xs text-white/70 mb-1">Total Duration</p>
            <p className="text-xl font-bold">{Math.round(resourceTasks.reduce((s, t) => s + (t.duration_words || 0), 0)).toLocaleString("en-IN")}</p>
          </div>
          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-4 text-white" data-testid="res-avg-card">
            <p className="text-xs text-white/70 mb-1">Avg per Task</p>
            <p className="text-xl font-bold">{resourceTasks.length > 0 ? fmtFull(resourceTasks.reduce((s, t) => s + (t.amount || 0), 0) / resourceTasks.length) : "₹0"}</p>
          </div>
        </div>

        {/* Task Table */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <FileText className="w-4 h-4 text-indigo-600" />Task List
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="resource-task-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">#</th>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Episode</th>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Task Type</th>
                    <th className="text-center py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Season</th>
                    <th className="text-right py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Duration</th>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">File Received On</th>
                    <th className="text-right py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Rate</th>
                    <th className="text-right py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Amount</th>
                    <th className="text-center py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Edit</th>
                  </tr>
                </thead>
                <tbody>
                  {resourceTasks.map((t, idx) => (
                    <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`task-row-${idx}`}>
                      <td className="py-2.5 px-3 text-xs text-slate-400">{idx + 1}</td>
                      <td className="py-2.5 px-3">
                        <p className="text-sm font-medium text-slate-800 truncate max-w-[200px]">{t.episode_title || "-"}</p>
                        <p className="text-[10px] text-slate-400">Ep {t.episode_number || "-"}</p>
                      </td>
                      <td className="py-2.5 px-3">
                        <Badge variant="secondary" className="text-[10px]">{t.task || "-"}</Badge>
                      </td>
                      <td className="py-2.5 px-3 text-center text-sm text-slate-700">S{t.season_number || "-"}</td>
                      <td className="py-2.5 px-3 text-right text-sm text-slate-700">{t.duration_words ?? "-"}</td>
                      <td className="py-2.5 px-3 text-sm text-slate-600">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-3 h-3 text-slate-400" />
                          {fmtDate(t.file_received_on)}
                        </div>
                      </td>
                      <td className="py-2.5 px-3 text-right text-sm text-slate-700">
                        {t.rate ? `₹${t.rate}` : "-"}
                        {t.rate_type === "flat" && <span className="text-[9px] text-slate-400 ml-1">flat</span>}
                      </td>
                      <td className="py-2.5 px-3 text-right text-sm font-semibold text-slate-900">{t.amount ? fmtFull(t.amount) : "-"}</td>
                      <td className="py-2.5 px-3 text-center">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(t)} data-testid={`edit-task-btn-${idx}`}>
                          <Pencil className="w-3.5 h-3.5 text-slate-500" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {resourceTasks.length === 0 && (
                    <tr><td colSpan={9} className="py-8 text-center text-sm text-slate-400">No tasks found</td></tr>
                  )}
                </tbody>
                {resourceTasks.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-slate-300 bg-slate-50">
                      <td className="py-3 px-3" />
                      <td className="py-3 px-3 text-sm font-bold text-slate-800">Total ({resourceTasks.length} tasks)</td>
                      <td className="py-3 px-3" />
                      <td className="py-3 px-3" />
                      <td className="py-3 px-3 text-right text-sm font-bold text-slate-800">{Math.round(resourceTasks.reduce((s, t) => s + (t.duration_words || 0), 0)).toLocaleString("en-IN")}</td>
                      <td className="py-3 px-3" />
                      <td className="py-3 px-3" />
                      <td className="py-3 px-3 text-right text-sm font-bold text-slate-900">{fmtFull(resourceTasks.reduce((s, t) => s + (t.amount || 0), 0))}</td>
                      <td className="py-3 px-3" />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Edit Task Dialog */}
        <Dialog open={!!editingTask} onOpenChange={(open) => { if (!open) setEditingTask(null); }}>
          <DialogContent className="max-w-md" data-testid="edit-task-dialog">
            <DialogHeader>
              <DialogTitle className="text-base">Edit Task</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Duration / Words</Label>
                  <Input type="number" value={editData.duration_words} onChange={e => setEditData({...editData, duration_words: e.target.value})} className="h-8 text-sm" data-testid="edit-duration" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Rate</Label>
                  <Input type="number" value={editData.rate} onChange={e => setEditData({...editData, rate: e.target.value})} className="h-8 text-sm" data-testid="edit-rate" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Rate Type</Label>
                  <Select value={editData.rate_type} onValueChange={v => setEditData({...editData, rate_type: v})}>
                    <SelectTrigger className="h-8 text-sm" data-testid="edit-rate-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="per_unit">Per Unit</SelectItem>
                      <SelectItem value="flat">Flat</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Computed Amount</Label>
                  <div className="h-8 flex items-center text-sm font-semibold text-indigo-600">{fmtFull(editAmount)}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Episode Title</Label>
                  <Input value={editData.episode_title} onChange={e => setEditData({...editData, episode_title: e.target.value})} className="h-8 text-sm" data-testid="edit-episode-title" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Episode #</Label>
                  <Input value={editData.episode_number} onChange={e => setEditData({...editData, episode_number: e.target.value})} className="h-8 text-sm" data-testid="edit-episode-number" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Season #</Label>
                  <Input value={editData.season_number} onChange={e => setEditData({...editData, season_number: e.target.value})} className="h-8 text-sm" data-testid="edit-season" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Task Type</Label>
                  <Input value={editData.task} onChange={e => setEditData({...editData, task: e.target.value})} className="h-8 text-sm" data-testid="edit-task-type" />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => setEditingTask(null)} data-testid="edit-cancel-btn">Cancel</Button>
                <Button size="sm" onClick={saveEdit} disabled={saving} data-testid="edit-save-btn">
                  <Save className="w-3.5 h-3.5 mr-1" />{saving ? "Saving..." : "Save"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  /* =============================== */
  /* LEVEL 2: Project Detail         */
  /* =============================== */
  if (selectedProject && projectDetail) {
    return (
      <div className="p-6" data-testid="project-detail-view">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="outline" size="sm" onClick={() => { setSelectedProject(null); setSelectedSeason("all"); }} data-testid="back-to-projects-btn">
            <ChevronLeft className="w-4 h-4 mr-1" />Back
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900">{projectDetail.title}</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {projectDetail.season_count > 1 ? `${projectDetail.season_count} seasons` : "1 season"} &middot; {projectDetail.task_count} tasks &middot; {projectDetail.resource_count} resources
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchData} data-testid="detail-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />Refresh
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-gradient-to-br from-indigo-500 to-violet-600 rounded-2xl p-5 text-white" data-testid="detail-expense-card">
            <div className="flex items-center gap-2 mb-2"><IndianRupee className="w-4 h-4 text-white/70" /><span className="text-xs text-white/70">Total Expense</span></div>
            <p className="text-2xl font-bold">{fmt(projectDetail.total_amount)}</p>
          </div>
          <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-5 text-white" data-testid="detail-resources-card">
            <div className="flex items-center gap-2 mb-2"><Users className="w-4 h-4 text-white/70" /><span className="text-xs text-white/70">Resources</span></div>
            <p className="text-2xl font-bold">{projectDetail.resource_count}</p>
          </div>
          <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl p-5 text-white" data-testid="detail-tasks-card">
            <div className="flex items-center gap-2 mb-2"><FileText className="w-4 h-4 text-white/70" /><span className="text-xs text-white/70">Tasks</span></div>
            <p className="text-2xl font-bold">{projectDetail.task_count}</p>
          </div>
          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-5 text-white" data-testid="detail-top-earner-card">
            <div className="flex items-center gap-2 mb-2"><Trophy className="w-4 h-4 text-white/70" /><span className="text-xs text-white/70">Top Earner</span></div>
            <p className="text-lg font-bold truncate">{projectDetail.top_earner?.name || "-"}</p>
            <p className="text-sm text-white/70">{projectDetail.top_earner ? fmt(projectDetail.top_earner.amount) : ""}</p>
          </div>
        </div>

        {/* Season Breakdown */}
        {projectDetail.season_count > 1 && (
          <Card className="mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-indigo-600" />Season Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="season-table">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Season</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Resources</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Tasks</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Duration</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Expense</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500 uppercase">% Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projectDetail.seasonList.map((s, i) => (
                      <tr
                        key={s.season}
                        className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors ${selectedSeason === s.season ? "bg-indigo-50" : ""}`}
                        onClick={() => setSelectedSeason(prev => prev === s.season ? "all" : s.season)}
                        data-testid={`season-row-${i}`}
                      >
                        <td className="py-2.5 px-3">
                          <div className="flex items-center gap-2">
                            <Badge variant={selectedSeason === s.season ? "default" : "secondary"} className="text-xs">S{s.season}</Badge>
                          </div>
                        </td>
                        <td className="py-2.5 px-3 text-right text-sm text-slate-700">{Object.keys(s.resources).length}</td>
                        <td className="py-2.5 px-3 text-right text-sm text-slate-700">{s.tasks}</td>
                        <td className="py-2.5 px-3 text-right text-sm text-slate-700">{Math.round(s.duration).toLocaleString("en-IN")}</td>
                        <td className="py-2.5 px-3 text-right text-sm font-semibold text-slate-900">{fmtFull(s.amount)}</td>
                        <td className="py-2.5 px-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${projectDetail.total_amount > 0 ? (s.amount / projectDetail.total_amount) * 100 : 0}%` }} />
                            </div>
                            <span className="text-xs text-slate-500 w-10 text-right">{projectDetail.total_amount > 0 ? ((s.amount / projectDetail.total_amount) * 100).toFixed(1) : "0"}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {selectedSeason !== "all" && (
                <div className="mt-2 flex items-center gap-2">
                  <Badge className="text-xs">Filtering: Season {selectedSeason}</Badge>
                  <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setSelectedSeason("all")}>
                    <X className="w-3 h-3 mr-1" />Clear filter
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <Card className="lg:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Users className="w-4 h-4 text-indigo-600" />
                Resource Expenses
                {selectedSeason !== "all" && <Badge variant="secondary" className="text-[10px]">S{selectedSeason}</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {detailResources.length > 0 ? (
                <ResponsiveContainer width="100%" height={Math.max(250, detailResources.slice(0, 15).length * 32)}>
                  <BarChart data={detailResources.slice(0, 15)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke={chartGrid(dark)} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: chartAxis(dark) }} stroke={chartAxis(dark)} tickFormatter={v => fmt(v)} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: chartAxis(dark) }} stroke={chartAxis(dark)} width={110} />
                    <Tooltip formatter={(v) => [fmtFull(v), "Expense"]} contentStyle={chartTooltip(dark)} />
                    <Bar dataKey="amount" fill="#6366f1" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[250px] flex items-center justify-center text-slate-400 text-sm">No data</div>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-emerald-600" />Task Type Split
              </CardTitle>
            </CardHeader>
            <CardContent>
              {detailPieData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={180}>
                    <RechartsPie>
                      <Pie data={detailPieData} cx="50%" cy="50%" innerRadius={45} outerRadius={72} paddingAngle={4} dataKey="value">
                        {detailPieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                      </Pie>
                      <Tooltip formatter={v => [fmtFull(v), "Expense"]} contentStyle={chartTooltip(dark)} />
                    </RechartsPie>
                  </ResponsiveContainer>
                  <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2">
                    {detailPieData.map((e, i) => (
                      <div key={i} className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ background: e.color }} />
                        <span className="text-[11px] text-slate-500">{e.name} ({fmt(e.value)})</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-slate-400 text-sm">No data</div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Resource Table — clickable rows */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <IndianRupee className="w-4 h-4 text-indigo-600" />Resources on this Project
              {selectedSeason !== "all" && <Badge variant="secondary" className="text-[10px]">S{selectedSeason}</Badge>}
              <Badge variant="secondary" className="text-[10px]">{detailResources.length} resources</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="project-resource-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">#</th>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Resource</th>
                    <th className="text-right py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Tasks</th>
                    <th className="text-right py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Duration</th>
                    <th className="text-right py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Expense</th>
                    <th className="text-right py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">% Share</th>
                    <th className="text-center py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider"></th>
                  </tr>
                </thead>
                <tbody>
                  {detailResources.map((r, idx) => (
                    <tr
                      key={r.email}
                      className="border-b border-slate-100 hover:bg-indigo-50 transition-colors cursor-pointer"
                      onClick={() => setSelectedResource(r.email)}
                      data-testid={`resource-row-${idx}`}
                    >
                      <td className="py-2.5 px-3 text-xs text-slate-400">{idx + 1}</td>
                      <td className="py-2.5 px-3">
                        <div className="flex items-center gap-2">
                          <div>
                            <p className="text-sm font-medium text-slate-800">{r.name}</p>
                            <p className="text-[11px] text-slate-400">{r.email}</p>
                          </div>
                          {idx === 0 && <Badge className="bg-amber-100 text-amber-700 text-[9px] border-0 hover:bg-amber-100">Top Earner</Badge>}
                        </div>
                      </td>
                      <td className="py-2.5 px-3 text-right text-sm text-slate-700">{r.tasks}</td>
                      <td className="py-2.5 px-3 text-right text-sm text-slate-700">{Math.round(r.duration).toLocaleString("en-IN")}</td>
                      <td className="py-2.5 px-3 text-right text-sm font-semibold text-slate-900">{fmtFull(r.amount)}</td>
                      <td className="py-2.5 px-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${detailTotalAmount > 0 ? (r.amount / detailTotalAmount) * 100 : 0}%` }} />
                          </div>
                          <span className="text-xs text-slate-500 w-10 text-right">{detailTotalAmount > 0 ? ((r.amount / detailTotalAmount) * 100).toFixed(1) : "0"}%</span>
                        </div>
                      </td>
                      <td className="py-2.5 px-3 text-center"><ChevronRight className="w-4 h-4 text-slate-400 inline-block" /></td>
                    </tr>
                  ))}
                </tbody>
                {detailResources.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-slate-300 bg-slate-50">
                      <td className="py-3 px-3" />
                      <td className="py-3 px-3 text-sm font-bold text-slate-800">Total</td>
                      <td className="py-3 px-3 text-right text-sm font-bold text-slate-800">{detailResources.reduce((s, r) => s + r.tasks, 0)}</td>
                      <td className="py-3 px-3 text-right text-sm font-bold text-slate-800">{Math.round(detailResources.reduce((s, r) => s + r.duration, 0)).toLocaleString("en-IN")}</td>
                      <td className="py-3 px-3 text-right text-sm font-bold text-slate-900">{fmtFull(detailResources.reduce((s, r) => s + r.amount, 0))}</td>
                      <td className="py-3 px-3 text-right text-xs text-slate-500">100%</td>
                      <td className="py-3 px-3" />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  /* =============================== */
  /* LEVEL 1: Project List           */
  /* =============================== */
  return (
    <div className="p-6" data-testid="project-dashboard">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Project Dashboard</h1>
          <p className="text-sm text-slate-500 mt-0.5">Expense breakdown by project with season drill-down</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" />Refresh
        </Button>
      </div>

      <DashboardFilters onFiltersChange={handleFiltersChange} onGranularityChange={() => {}} />

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-indigo-500 to-violet-600 rounded-2xl p-5 text-white" data-testid="total-projects-card">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center"><FolderOpen className="w-5 h-5" /></div>
            <Badge className="bg-white/20 text-white text-[10px] hover:bg-white/20">Projects</Badge>
          </div>
          <p className="text-2xl font-bold">{filtered?.has_filters ? filtered.top_projects?.length || 0 : totalProjects}</p>
          <p className="text-sm text-white/70 mt-1">Total Projects</p>
        </div>
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-5 text-white" data-testid="total-project-expense-card">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center"><IndianRupee className="w-5 h-5" /></div>
            <Badge className="bg-white/20 text-white text-[10px] hover:bg-white/20">Total</Badge>
          </div>
          <p className="text-2xl font-bold">{fmt(filtered?.has_filters ? filtered.total_amount : totalExpense)}</p>
          <p className="text-sm text-white/70 mt-1">Total Expenses</p>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl p-5 text-white" data-testid="avg-project-card">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center"><BarChart3 className="w-5 h-5" /></div>
            <Badge className="bg-white/20 text-white text-[10px] hover:bg-white/20">Average</Badge>
          </div>
          <p className="text-2xl font-bold">{fmt(avgPerProject)}</p>
          <p className="text-sm text-white/70 mt-1">Avg per Project</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-5 text-white" data-testid="top-project-card">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center"><Trophy className="w-5 h-5" /></div>
            <Badge className="bg-white/20 text-white text-[10px] hover:bg-white/20">Highest</Badge>
          </div>
          <p className="text-lg font-bold truncate">{topProject?.title || "-"}</p>
          <p className="text-sm text-white/70 mt-1">{topProject ? fmt(topProject.total_amount) : ""}</p>
        </div>
      </div>

      {/* Top 10 bar chart */}
      <Card className="mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-indigo-600" />Top 10 Projects by Expense
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(fTopProjects || topProjectsChart).length > 0 ? (
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={fTopProjects || topProjectsChart} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke={chartGrid(dark)} />
                <XAxis type="number" tick={{ fontSize: 11, fill: chartAxis(dark) }} stroke={chartAxis(dark)} tickFormatter={v => fmt(v)} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: chartAxis(dark) }} stroke={chartAxis(dark)} width={140} />
                <Tooltip formatter={(v) => [fmtFull(v), "Expense"]} labelFormatter={(l, p) => p?.[0]?.payload?.fullName || l} contentStyle={chartTooltip(dark)} />
                <Bar dataKey="amount" fill="#6366f1" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[340px] flex items-center justify-center text-slate-400 text-sm">No data</div>
          )}
        </CardContent>
      </Card>

      {/* Row 2: Language Breakdown + Task Type Distribution + Resource Efficiency */}
      {filtered && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          {/* Language Breakdown Pie */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><BarChart3 className="w-4 h-4 text-violet-600" />By Language</CardTitle></CardHeader>
            <CardContent>
              {filtered.language_breakdown?.length > 0 ? (
                <div className="flex items-center">
                  <ResponsiveContainer width="55%" height={200}>
                    <RechartsPie>
                      <Pie data={filtered.language_breakdown.slice(0, 8).map(l => ({ name: l.language, value: l.count }))} cx="50%" cy="50%" outerRadius={75} innerRadius={40} paddingAngle={2} dataKey="value">
                        {filtered.language_breakdown.slice(0, 8).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={chartTooltip(dark)} />
                    </RechartsPie>
                  </ResponsiveContainer>
                  <div className="w-[45%] space-y-1 pl-2">
                    {filtered.language_breakdown.slice(0, 6).map((l, i) => (
                      <div key={i} className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} /><span className="text-[10px] text-slate-600 truncate">{l.language}</span><span className="text-[10px] font-semibold text-slate-800 ml-auto">{l.count}</span></div>
                    ))}
                  </div>
                </div>
              ) : <div className="h-[200px] flex items-center justify-center text-sm text-slate-400">No data</div>}
            </CardContent>
          </Card>

          {/* Task Type Distribution */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><FileText className="w-4 h-4 text-blue-600" />By Task Type</CardTitle></CardHeader>
            <CardContent>
              {filtered.task_type_distribution?.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={filtered.task_type_distribution.sort((a, b) => b.amount - a.amount).slice(0, 8).map(t => ({ name: t.name, amount: t.amount, count: t.count }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartGrid(dark)} />
                    <XAxis dataKey="name" tick={{ fontSize: 9, fill: chartAxis(dark) }} stroke={chartAxis(dark)} />
                    <YAxis tick={{ fontSize: 10, fill: chartAxis(dark) }} stroke={chartAxis(dark)} tickFormatter={v => fmt(v)} />
                    <Tooltip formatter={(v, n) => [n === "amount" ? fmtFull(v) : v, n === "amount" ? "Expense" : "Tasks"]} contentStyle={chartTooltip(dark)} />
                    <Bar dataKey="amount" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Expense" />
                  </BarChart>
                </ResponsiveContainer>
              ) : <div className="h-[200px] flex items-center justify-center text-sm text-slate-400">No data</div>}
            </CardContent>
          </Card>

          {/* Resource Efficiency */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Users className="w-4 h-4 text-emerald-600" />Top Earners (Avg/Task)</CardTitle></CardHeader>
            <CardContent>
              {filtered.resource_efficiency?.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={filtered.resource_efficiency.slice(0, 8).map(r => ({ name: r.name.split("@")[0].slice(0, 10), avg: r.avg_per_task }))} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke={chartGrid(dark)} />
                    <XAxis type="number" tick={{ fontSize: 10, fill: chartAxis(dark) }} stroke={chartAxis(dark)} tickFormatter={v => `₹${v}`} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: chartAxis(dark) }} stroke={chartAxis(dark)} width={70} />
                    <Tooltip formatter={(v) => [`₹${v.toLocaleString("en-IN")}`, "Avg/Task"]} contentStyle={chartTooltip(dark)} />
                    <Bar dataKey="avg" fill="#10b981" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <div className="h-[200px] flex items-center justify-center text-sm text-slate-400">No data</div>}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Project Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <FolderOpen className="w-4 h-4 text-indigo-600" />All Projects
              <Badge variant="secondary" className="text-[10px]">{filteredProjects.length} projects</Badge>
            </CardTitle>
            <div className="relative w-56">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input placeholder="Search project..." value={tableSearch} onChange={e => setTableSearch(e.target.value)} className="pl-8 h-8 text-xs" data-testid="project-table-search" />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="project-expense-table">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">#</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Project</th>
                  <th className="text-center py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Seasons</th>
                  <th className="text-right py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort("resource_count")}>
                    <span className="inline-flex items-center gap-1">Resources <SortIcon field="resource_count" /></span>
                  </th>
                  <th className="text-right py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort("task_count")}>
                    <span className="inline-flex items-center gap-1">Tasks <SortIcon field="task_count" /></span>
                  </th>
                  <th className="text-right py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort("total_amount")}>
                    <span className="inline-flex items-center gap-1">Expense <SortIcon field="total_amount" /></span>
                  </th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Top Earner</th>
                  <th className="text-center py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider"></th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.map((p, idx) => (
                  <tr key={p.title} className="border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => { setSelectedProject(p.title); setSelectedSeason("all"); }} data-testid={`project-row-${idx}`}>
                    <td className="py-2.5 px-3 text-xs text-slate-400">{idx + 1}</td>
                    <td className="py-2.5 px-3">
                      <p className="text-sm font-medium text-slate-800">{p.title}</p>
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      <Badge variant="secondary" className="text-[10px]">{p.season_count}</Badge>
                    </td>
                    <td className="py-2.5 px-3 text-right text-sm text-slate-700">{p.resource_count}</td>
                    <td className="py-2.5 px-3 text-right text-sm text-slate-700">{p.task_count}</td>
                    <td className="py-2.5 px-3 text-right text-sm font-semibold text-slate-900">{fmtFull(p.total_amount)}</td>
                    <td className="py-2.5 px-3">
                      {p.top_earner && (
                        <div>
                          <p className="text-xs font-medium text-slate-700">{p.top_earner.name}</p>
                          <p className="text-[10px] text-slate-400">{fmt(p.top_earner.amount)}</p>
                        </div>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-center"><ChevronRight className="w-4 h-4 text-slate-400 inline-block" /></td>
                  </tr>
                ))}
                {filteredProjects.length === 0 && (
                  <tr><td colSpan={8} className="py-8 text-center text-sm text-slate-400">No projects found</td></tr>
                )}
              </tbody>
              {filteredProjects.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-slate-300 bg-slate-50">
                    <td className="py-3 px-3" />
                    <td className="py-3 px-3 text-sm font-bold text-slate-800">Total ({filteredProjects.length} projects)</td>
                    <td className="py-3 px-3" />
                    <td className="py-3 px-3 text-right text-sm font-bold text-slate-800">{filteredProjects.reduce((s, p) => s + p.resource_count, 0)}</td>
                    <td className="py-3 px-3 text-right text-sm font-bold text-slate-800">{filteredProjects.reduce((s, p) => s + p.task_count, 0)}</td>
                    <td className="py-3 px-3 text-right text-sm font-bold text-slate-900">{fmtFull(filteredProjects.reduce((s, p) => s + p.total_amount, 0))}</td>
                    <td colSpan={2} className="py-3 px-3" />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProjectDashboard;
